import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PlayButtonLight } from '@onesource/icons';
import type { TContentBlockBase } from '@onesource/schemas';
import { EContentStatus } from '@onesource/schemas';
import { useGetVideoPlayedBlocks, useGetVideoWatchCounts } from '@onesource/services';
import { MdOutlineVisibility } from 'react-icons/md';
import { DraftBadge } from 'src/components/draft-badge';

interface VideoPlaylistProps {
  videoBlocks: TContentBlockBase[];
  lessonId: string;
  selectedBlockId: string;
  onSelect: (block: TContentBlockBase) => void;
}

// Figma palette (Dashboard - RYC pro Area, node 4882:2290): pill colors follow
// the tone-20 background / tone-50 text pairs; the accent red matches the play
// glyph, not the app theme primary.
const ACCENT_RED = '#FF2451';
const ROW_SELECTED_BG = '#FFF7F5'; // Browns/20
const CARD_BORDER = '#f8e8e4';

const STATUS_STYLES = {
  nowPlaying: { label: 'NOW PLAYING', bgcolor: '#FFB9C8', color: ACCENT_RED }, // Red/20
  watched: { label: 'WATCHED', bgcolor: '#C8F7E9', color: '#4CBF9F' },
  continueWatching: { label: 'CONTINUE WATCHING', bgcolor: '#FFD1CC', color: '#FF7262' }, // Orange/20 / Orange/50
} as const;

const StatusPill = ({ status }: { status: keyof typeof STATUS_STYLES }) => {
  const { label, bgcolor, color } = STATUS_STYLES[status];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        bgcolor,
        color,
        borderRadius: '15px',
        px: '6px',
        py: '1px',
        fontSize: { xs: 8, md: 6 },
        fontWeight: 600,
        letterSpacing: '0.3px',
        lineHeight: { xs: undefined, md: '8.2px' },
        whiteSpace: 'nowrap',
      }}
    >
      {status === 'watched' && <MdOutlineVisibility size={7} />}
      {label}
    </Box>
  );
};

/**
 * The video playlist of the inline-playlist lesson template. Lists every video
 * block of the lesson with its watch state and switches the hero player on
 * click. Rendered beside the player on desktop and as a stacked card below the
 * description on mobile (see the RYC pro area Figma, node 4882:2254).
 */
export const VideoPlaylist = ({ videoBlocks, lessonId, selectedBlockId, onSelect }: VideoPlaylistProps) => {
  // Watch counts (finished views) drive the WATCHED pill; the VIDEO_PLAY
  // activity records drive CONTINUE WATCHING. A play record only exists once
  // playback actually started, so a video that was merely scrolled into view
  // or selected never gets the pill — the lesson progress's lastBlockId can't
  // tell those apart, which is why it isn't used here.
  const { data: watchCounts } = useGetVideoWatchCounts(lessonId);
  const { data: playedBlocks } = useGetVideoPlayedBlocks(lessonId);

  const getStatus = (block: TContentBlockBase): keyof typeof STATUS_STYLES | null => {
    if (block.id === selectedBlockId) {
      return 'nowPlaying';
    }
    // A completed watch wins over a later partial replay.
    if ((watchCounts?.[block.id] ?? 0) > 0) {
      return 'watched';
    }
    if (playedBlocks?.[block.id]) {
      return 'continueWatching';
    }
    return null;
  };

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '15px',
        border: `1px solid ${CARD_BORDER}`,
        pt: 2,
        pb: 1,
        height: { xs: 'auto', md: '100%' },
        overflowY: 'auto',
      }}
    >
      <Typography
        sx={{
          px: 2,
          mb: { xs: 1.4, md: 1.2 },
          fontSize: { xs: '12px', md: '12px' },
          letterSpacing: '2.76px',
          color: '#AAAAAA',
        }}
      >
        VIDEO PLAYLIST({videoBlocks.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {videoBlocks.map((block, idx) => {
          const status = getStatus(block);
          const isSelected = block.id === selectedBlockId;
          return (
            <Box
              key={block.id}
              onClick={() => onSelect(block)}
              sx={{
                pl: 2,
                pr: 0.5,
                py: '5px',
                cursor: 'pointer',
                bgcolor: isSelected ? ROW_SELECTED_BG : 'transparent',
                '&:hover': { bgcolor: ROW_SELECTED_BG },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', pl: 2, mb: { xs: '4px', md: '5px' } }}>
                <Typography
                  sx={{
                    fontSize: { xs: '9px', md: '9px' },
                    fontWeight: 400,
                    letterSpacing: { xs: '0.3px', md: '0.3px' },
                    lineHeight: '12px',
                    color: '#A8A8A8',
                  }}
                >
                  Video {idx + 1}
                </Typography>
                {status && <StatusPill status={status} />}
                {block.status !== EContentStatus.PUBLISHED && <DraftBadge />}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                <PlayButtonLight fontSize={12} color={ACCENT_RED} style={{ marginTop: 3, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: { xs: '12px', md: '12px' },
                    lineHeight: '18px',
                    fontWeight: isSelected ? 700 : 400,
                    color: 'common.black',
                  }}
                >
                  {block.title || `Video ${idx + 1}`}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
